import React, { CSSProperties, useMemo, useState, useRef } from 'react';
import { Menu, MenuItem, ClickAwayListener } from '@mui/material';
import clsx from 'clsx';
import { AccessTime } from '@mui/icons-material';
import { TaskItemDuration } from '@demind-inc/core';
import { useTranslation } from 'react-i18next';

import './DurationField.scss';

interface DurationFieldProps {
  value: TaskItemDuration;
  onChange: (value: TaskItemDuration) => void;
  intervals?: number[]; // Duration options in minutes
  className?: string;
  showIcon?: boolean;
  textFieldClassName?: string;
  menuClassName?: string;
  disabled?: boolean;
  textStyle?: CSSProperties;
  shortFormat?: boolean;
}

// Default duration options in minutes
const DEFAULT_DURATION_OPTIONS = [5, 10, 15, 30, 45, 60, 75, 90, 105, 120, 135, 150, 165, 180];

// Format duration for display
const formatDuration = (
  t: (key: string, options?: Record<string, unknown>) => string,
  minutes: number,
  shortFormat: boolean = false
): string => {
  if (minutes < 60) {
    return shortFormat
      ? t('task.duration.minutesShort', { minutes })
      : t('task.duration.minutesLong', { minutes });
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (remainingMinutes === 0) {
    return t('task.duration.hoursShort', { hours });
  }
  return shortFormat
    ? t('task.duration.hoursMinutesShort', { hours, minutes: remainingMinutes })
    : t('task.duration.hoursMinutesLong', { hours, minutes: remainingMinutes });
};

export const DurationField: React.FC<DurationFieldProps> = ({
  value,
  onChange,
  intervals = DEFAULT_DURATION_OPTIONS,
  className,
  showIcon = true,
  textFieldClassName,
  menuClassName,
  disabled = false,
  textStyle,
  shortFormat = false,
}) => {
  const { t } = useTranslation();
  const [internalOpen, setInternalOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  // Convert duration options to display strings
  const durationOptions = useMemo(
    () =>
      intervals.map((minutes) => ({
        value: minutes,
        label: formatDuration(t, minutes, shortFormat),
      })),
    [intervals, shortFormat, t]
  );

  // Current display value
  const displayValue = useMemo(() => {
    if (!value || value.amount === 0) return '';
    return formatDuration(t, value.amount, shortFormat);
  }, [shortFormat, t, value]);

  const handleMenuItemClick = (minutes: number) => {
    onChange({
      amount: minutes,
      unit: 'minute',
    });
    setInternalOpen(false);
  };

  const handleOpen = () => {
    if (!disabled) {
      setInternalOpen(true);
    }
  };

  return (
    <div
      className={clsx(
        'task-duration-field',
        className,
        disabled && 'task-duration-field--disabled'
      )}
    >
      <ClickAwayListener
        onClickAway={() => {
          setInternalOpen(false);
        }}
      >
        <div ref={anchorRef} className="task-duration-field__container">
          <div
            onClick={handleOpen}
            className={clsx('task-duration-field__display', textFieldClassName)}
          >
            {showIcon && (
              <AccessTime className="task-duration-field__dropdown-button" style={textStyle} />
            )}
            <span className="task-duration-field__text" style={textStyle}>
              {displayValue || t('task.duration.selectDuration')}
            </span>
          </div>
        </div>
      </ClickAwayListener>

      <Menu
        anchorEl={anchorRef.current}
        open={internalOpen}
        onClose={() => {
          setInternalOpen(false);
        }}
        className={clsx('task-duration-field__menu', menuClassName)}
        PaperProps={{
          className: 'task-duration-field__paper',
        }}
        MenuListProps={{
          className: 'task-duration-field__menu-list',
        }}
      >
        {durationOptions.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => handleMenuItemClick(option.value)}
            className="task-duration-field__menu-item"
            selected={value && value.amount === option.value}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
};
